<template>
  <div class="payment-create main-content">
    <form action="" @submit.prevent="submitPrepaymentRequestData">
      <div class="page-header">
        <h3 class="page-title">
          {{ $t("pos.purchase_order_financing_request") }}
        </h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/supplier/dashboard">{{
              $t("general.home")
            }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/supplier/purchase-orders">{{
              $t("navigation.pos")
            }}</router-link>
          </li>
          <li class="breadcrumb-item">
            {{ $t("pos.purchase_order_financing") }}
          </li>
        </ol>
        <div class="page-actions row">
          <div class="col-xs-2">
            <base-button
              :loading="isLoading"
              size="large"
              icon="save"
              color="theme"
              type="submit"
            >
              {{ $t("pos.send_po_financing_request") }}
            </base-button>
          </div>
        </div>
      </div>
      <SinglePurchaseOrderFinanceRow :fetchData="fetchData" />
      <!-- <SingleInvoiceRow v-else :fetchData="fetchData" /> -->

      <div class="payment-card card">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t("prepayments.date") }}</label
                ><span class="text-danger"> *</span>
                <base-date-picker
                  v-model="formData.prepayment_date"
                  :invalid="$v.formData.prepayment_date.$error"
                  :calendar-button="true"
                  :disabled="true"
                  calendar-button-icon="calendar"
                  @change="$v.formData.prepayment_date.$touch()"
                />
                <div v-if="$v.formData.prepayment_date.$error">
                  <span
                    v-if="!$v.formData.prepayment_date.required"
                    class="text-danger"
                    >{{ $t("validation.required") }}</span
                  >
                </div>
              </div>
            </div>

            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.prepayment_request_number")
                }}</label
                ><span class="text-danger"> *</span>
                <base-prefix-input
                  :readonly="true"
                  :invalid="$v.requestNumAttribute.$error"
                  v-model.trim="requestNumAttribute"
                  :prefix="requestPrefix"
                  @input="$v.requestNumAttribute.$touch()"
                />
                <div v-if="$v.requestNumAttribute.$error">
                  <span
                    v-if="!$v.requestNumAttribute.required"
                    class="text-danger"
                    >{{ $tc("validation.required") }}</span
                  >
                  <span
                    v-if="!$v.requestNumAttribute.numeric"
                    class="text-danger"
                    >{{ $tc("validation.numbers_only") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t("prepayments.amount") }}</label
                ><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :class="{ invalid: $v.formData.amount.$error }"
                    v-model="amount"
                    v-bind="customerCurrency"
                    class="input-field"
                  />
                </div>
                <div v-if="$v.formData.amount.$error">
                  <span
                    v-if="!$v.formData.amount.required"
                    class="text-danger"
                    >{{ $t("validation.required") }}</span
                  >
                  <span
                    v-if="
                      !$v.formData.amount.between &&
                        $v.formData.amount.numeric &&
                        amount <= 0
                    "
                    class="text-danger"
                    >{{ $t("validation.payment_greater_than_zero") }}</span
                  >
                  <span
                    v-if="!$v.formData.amount.between && amount > 0"
                    class="text-danger"
                    >{{
                      $t("validation.request_greater_than_noimal_amount")
                    }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.payment_mode")
                }}</label>
                <base-select
                  v-model="formData.payment_method"
                  :options="paymentModes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('prepayments.select_payment_mode')"
                  label="name"
                >
                  <div slot="afterList">
                    <button
                      type="button"
                      class="list-add-button"
                      @click="addPaymentMode"
                    >
                      <font-awesome-icon class="icon" icon="cart-plus" />
                      <label>{{
                        $t("settings.customization.payments.add_payment_mode")
                      }}</label>
                    </button>
                  </div>
                </base-select>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.offer_requested_on_date")
                }}</label
                ><span class="text-danger"> *</span>
                <base-select
                  v-model="formData.supplier_offer_request"
                  :options="offer_request"
                  :allow-empty="false"
                  :show-labels="false"
                  :tabindex="5"
                  :placeholder="$t('prepayments.select_offer_request')"
                  label="name"
                  track-by="label"
                />
                <div v-if="$v.formData.supplier_offer_request.$error">
                  <span
                    v-if="!$v.formData.supplier_offer_request.required"
                    class="text-danger"
                    >{{ $t("validation.required") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("invoices.payment_date")
                }}</label
                ><span class="text-danger"> *</span>
                <base-date-picker
                  v-model="formData.payment_date"
                  :calendar-button="true"
                  calendar-button-icon="calendar"
                  :disabledDates="{
                    to: new Date(Date.now())
                  }"
                />
                <div v-if="$v.formData.payment_date.$error">
                  <span
                    v-if="!$v.formData.payment_date.required"
                    class="text-danger"
                    >{{ $t("validation.required") }}</span
                  >
                </div>
              </div>
            </div>
            <div
              v-if="
                formData.supplier_offer_request &&
                  formData.supplier_offer_request.label === 'select_a_date'
              "
              class="col-sm-6"
            >
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.offer_requested_on_date")
                }}</label>
                <base-date-picker
                  v-model="formData.supplier_offer_request_date"
                  :calendar-button="true"
                  calendar-button-icon="calendar"
                  :disabledDates="{
                    to: new Date(Date.now())
                  }"
                />
              </div>
            </div>
            <div class="col-sm-12">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.supplier_comment")
                }}</label>
                <base-text-area
                  v-model="formData.supplier_comment"
                  @input="$v.formData.supplier_comment.$touch()"
                />
                <div v-if="$v.formData.supplier_comment.$error">
                  <span
                    v-if="!$v.formData.supplier_comment.maxLength"
                    class="text-danger"
                    >{{ $t("validation.notes_maxlength") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-12">
              <div class="form-group collapse-button-container">
                <base-button
                  :loading="isLoading"
                  icon="save"
                  color="theme"
                  type="submit"
                  class="collapse-button"
                >
                  {{ $t("prepayments.save_prepayment") }}
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import SinglePurchaseOrderFinanceRow from "./SinglePurchaseOrderFinanceRow.vue";
import { mapActions, mapGetters } from "vuex";
import MultiSelect from "vue-multiselect";
import { validationMixin } from "vuelidate";
import moment from "moment";
const {
  required,
  between,
  maxLength,
  numeric
} = require("vuelidate/lib/validators");

export default {
  components: { MultiSelect, SinglePurchaseOrderFinanceRow },
  mixins: [validationMixin],
  data() {
    return {
      offer_request: [
        {
          id: 1,
          name: " As Soon as Possible",
          label: "as_soon_as_possible"
        },
        {
          id: 2,
          name: "Select a Date",
          label: "select_a_date"
        }
      ],
      // customerCurrencyDetail: null,
      noteReadProperty: false,
      userType: null,
      formData: {
        prepayment_request_number: null,
        prepayment_date: null,
        amount: 0,
        payment_method: null,
        purchase_order_id: null,
        supplier_comment: null,
        payment_method_id: null,
        supplier_offer_request: null,
        supplier_offer_request_date: null,
        payment_date: null
      },
      money: {
        decimal: ".",
        thousands: ",",
        prefix: "$ ",
        precision: 2,
        masked: false
      },
      customer: null,
      invoice: null,

      isLoading: false,
      maxPayableAmount: Number.MAX_SAFE_INTEGER,
      isSettingInitialData: true,
      requestNumAttribute: null,
      requestPrefix: "",
      advanceOption: true,
      fetchData: [],
      defaultInput: {
        decimal: ".",
        thousands: ",",
        prefix: "% ",
        precision: 2,
        masked: false
      },
      invoice_nominal_amount: 0
    };
  },
  computed: {
    ...mapGetters("currency", ["defaultCurrencyForInput"]),
    ...mapGetters("payment", ["paymentModes"]),
    amount: {
      get: function() {
        return this.formData.amount;
      },
      set: function(newValue) {
        if (newValue % 1 === 0) {
          this.formData.amount = newValue;
        } else {
          this.formData.amount = newValue.toFixed(2);
        }
      }
    },
    customerCurrency() {
      // if (this.customerCurrencyDetail) {
      //   // return {
      //   //   decimal: this.customerCurrencyDetail.decimal_separator,
      //   //   thousands: this.customerCurrencyDetail.thousand_separator,
      //   //   prefix: this.customerCurrencyDetail.symbol + " ",
      //   //   precision: this.customerCurrencyDetail.precision,
      //   //   masked: false
      //   // };
      // } else {
      return this.defaultCurrencyForInput;
      // }
    }
  },
  watch: {},
  created() {
    if (localStorage.getItem("role") === "super_admin") {
      this.userType = "super_admin";
    } else if (localStorage.getItem("account_type") === "anchor") {
      this.userType = "anchor";
    } else if (localStorage.getItem("account_type") === "supplier") {
      this.userType = "supplier";
    } else if (localStorage.getItem("account_type") === "funder") {
      this.userType = "funder";
    }
    this.loadPurchaseOrder();
  },
  validations() {
    return {
      formData: {
        prepayment_date: {
          required
        },
        amount: {
          required,
          between: between(1, this.maxPayableAmount + 1)
        },
        supplier_comment: {
          maxLength: maxLength(255)
        },
        supplier_offer_request: {
          required
        },
        payment_date: {
          required
        }
      },
      requestNumAttribute: {
        required,
        numeric
      }
    };
  },

  async mounted() {
    this.$nextTick(() => {
      this.loadData();
    });
  },
  methods: {
    ...mapActions("po", ["SinglePOArray"]),
    ...mapActions("prepayment", [
      "fetchCreatePayment",
      "addPayment",
      "updatePayment",
      "fetchEditPaymentData",
      "createPOPrepaymentRequest",
      "addPOPrepaymentRequest",
      "editPrepaymentRequest",
      "updatePrepaymentRequest"
    ]),
    ...mapActions("modal", ["openModal"]),
    ...mapActions("LoanRequest", ["loanRequestStatus"]),
    async loadPurchaseOrder() {
      let response = await this.SinglePOArray(this.$route.params.id);
      if (response.data) {
        this.fetchData = response.data.purchaseOrder;
      }
    },
    async addPaymentMode() {
      this.openModal({
        title: "Add Payment Mode",
        componentName: "PaymentMode"
      });
    },

    async loadData() {
      let data = {
        purchase_order_id: this.$route.params.id
      };
      let response = await this.createPOPrepaymentRequest(data);
      this.invoice = response.data.purchaseOrder;
      this.formData.purchase_order_id = response.data.purchaseOrder.id;
      this.invoice_nominal_amount = response.data.purchaseOrder.total;
      this.maxPayableAmount = this.invoice_nominal_amount;

      this.formData.amount = parseFloat(
        response.data.purchaseOrder.total
      ).toFixed(2);
      // this.customerCurrencyDetail = response.data.invoice.customer.currency;

      this.requestNumAttribute = response.data.nextRequestNumberAttribute;
      this.requestPrefix = response.data.request_prefix;
      this.formData.prepayment_date = moment(new Date()).toString();
      this.formData.supplier_offer_request_date = moment(
        new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)
      ).toString();
      this.formData.payment_date = moment(
        new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)
      ).toString();

      return true;
    },

    async submitPrepaymentRequestData() {
      this.$v.formData.$touch();
      if (this.$v.$invalid) {
        return true;
      }
      this.formData.prepayment_request_number =
        this.requestPrefix + "-" + this.requestNumAttribute;
      if (
        this.formData.supplier_offer_request &&
        this.formData.supplier_offer_request.label === "as_soon_as_possible"
      ) {
        this.formData.supplier_offer_request_date = null;
      }
      let data = {
        ...this.formData,
        payment_method_id: this.formData.payment_method
          ? this.formData.payment_method.id
          : null,
        prepayment_date: moment(this.formData.prepayment_date).format(
          "DD/MM/YYYY"
        ),
        payment_date: moment(this.formData.payment_date).format("DD/MM/YYYY"),
        supplier_offer_request: this.formData.supplier_offer_request
          ? this.formData.supplier_offer_request.label
          : null,
        supplier_offer_request_date: this.formData.supplier_offer_request_date
          ? moment(this.formData.supplier_offer_request_date).format(
              "DD/MM/YYYY"
            )
          : null
      };
      this.isLoading = true;
      try {
        let response = await this.addPOPrepaymentRequest(data);
        if (response.data.success) {
          window.toastr["success"](
            this.$t("prepayments.prepayment_request_send_message")
          );
          this.$router.push("/supplier/purchase-orders");

          this.isLoading = true;
          return true;
        }
        window.toastr["error"](response.data.message);
      } catch (err) {
        this.isLoading = false;
        window.toastr["error"](err.response.data.message);
      }
    }
  }
};
</script>
<style scoped>
.table-component {
  min-height: 0px;
}
</style>
